import { Component, OnInit } from '@angular/core';
import { RxFormBuilder, RxFormGroup } from '@rxweb/reactive-form-validators';
import { ConfirmationService, MessageService } from 'primeng/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { firstValueFrom } from 'rxjs';
import { FormValidatorService } from 'src/app/core/services/form-validator.service';
import { Filter, FilterOperation } from 'src/app/core/jsonapi/filter';
import { FilterBuilder } from 'src/app/core/jsonapi/filters-builder';
import { IParamsCollection } from 'src/app/core/jsonapi/interfaces/params-collection';
import { Queue } from '../../_types/queue';
import { QueueMember } from '../../_types/queue-member';
import { QueuesService } from '../../_services/queues.service';
import { QueuesMembersService } from '../../_services/queues-members.service';
import { QueueMemberAddComponent } from '../queue-member-add/queue-member-add.component';
import { ContractsService } from 'src/app/modules/companies/_services/contracts.service';

@Component({
    selector: 'app-queues-detail',
    templateUrl: './queues-detail.component.html',
    styleUrls: ['./queues-detail.component.css'],
    standalone: false
})
export class QueuesDetailComponent implements OnInit {
  public queue: Queue = new Queue();
  public queueFormGroup!: RxFormGroup;
  public members: QueueMember[] = [];
  public loading = false;
  public saving = false;
  public isNew = true;
  public isFromContract = false;

  public strategies = [
    { label: 'Ring all (timbra todos)', value: 'Ringall' },
    { label: 'Least recent (menos reciente)', value: 'Leastrecent' },
    { label: 'Fewest calls (menos llamadas)', value: 'Fewestcalls' },
    { label: 'Random', value: 'Random' },
    { label: 'Round robin memory', value: 'Rrmemory' },
    { label: 'Linear', value: 'Linear' },
    { label: 'Weighted random', value: 'Wrandom' },
    { label: 'Round robin ordered', value: 'Rrordered' }
  ];

  public yesNoOptions = [
    { label: 'Sí', value: 'Yes' },
    { label: 'No', value: 'No' }
  ];

  constructor(
    private queuesService: QueuesService,
    private queuesMembersService: QueuesMembersService,
    private config: DynamicDialogConfig,
    private dynamicDialogRef: DynamicDialogRef,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private dialogService: DialogService,
    private formBuilder: RxFormBuilder,
    private formValidationService: FormValidatorService,
    private contractsService: ContractsService
  ) {
    this.queueFormGroup = this.formBuilder.formGroup<Queue>(this.queue) as RxFormGroup;
  }

  ngOnInit() {
    const id = this.config.data?.id;
    if (id) {
      this.isNew = false;
      this.loadData(id);
    } else {
      this.isNew = true;
      this.queueFormGroup.patchValue({ strategy: 'Ringall' });
    }
  }

  async loadData(id: string) {
    this.loading = true;
    try {
      this.queue = await firstValueFrom(this.queuesService.get(id));
      this.queueFormGroup.patchValue(this.queue);
      await Promise.all([this.loadMembers(), this.checkIfFromContract(id)]);
      if (this.isFromContract) {
        this.queueFormGroup.disable();
      }
    } finally {
      this.loading = false;
    }
  }

  async checkIfFromContract(id: string) {
    try {
      const response = await firstValueFrom(this.contractsService.all({ page: { number: 1, size: 5000 } }));
      this.isFromContract = response.data.some((c: any) => c.id == id);
    } catch {
      this.isFromContract = false;
    }
  }

  async loadMembers() {
    const params: IParamsCollection = {
      page: { number: 1, size: 500 },
      filter: FilterBuilder.fromObject({
        queueName: new Filter(FilterOperation.equals, 'queueName', '', this.queue.name)
      }).build(),
      sort: ['interface']
    };
    const response = await firstValueFrom(this.queuesMembersService.all(params));
    this.members = response.data;
  }

  async save() {
    if (this.isFromContract) return;
    const valid = this.formValidationService.validateForm(this.queueFormGroup);
    if (!valid) {
      document.getElementById('queueForm')?.classList.add('formInvalid');
      return;
    }

    this.queue = Object.assign(this.queue, this.queueFormGroup.value);
    this.saving = true;
    this.queuesService.save(this.queue).subscribe({
      next: (saved: any) => {
        this.saving = false;
        this.messageService.add({ severity: 'success', detail: 'Cola guardada correctamente' });
        if (this.isNew) {
          this.queue = saved;
          this.isNew = false;
          this.loadMembers();
        }
      },
      error: (error: any) => {
        this.saving = false;
        this.messageService.add({ severity: 'error', detail: error?.error?.errors?.[0]?.title || error?.error?.title || 'Error al guardar la cola' });
      }
    });
  }

  addMember() {
    if (this.isFromContract) return;
    if (this.isNew) {
      this.messageService.add({ severity: 'warn', detail: 'Guarde la cola antes de añadir miembros' });
      return;
    }
    const ref = this.dialogService.open(QueueMemberAddComponent, {
      closable: true,
      modal: true,
      data: { queue: this.queue, existingMembers: this.members },
      header: 'Añadir miembro',
      width: '500px'
    });
    ref.onClose.subscribe((created: any) => {
      if (created) this.loadMembers();
    });
  }

  togglePause(member: any) {
    if (this.isFromContract) return;
    const newPaused = member.paused ? 0 : 1;
    const updated = new QueueMember();
    Object.assign(updated, member);
    updated.paused = newPaused;
    this.queuesMembersService.save(updated).subscribe({
      next: () => {
        member.paused = newPaused;
        this.messageService.add({
          severity: 'success',
          detail: newPaused ? 'Miembro pausado' : 'Miembro reanudado'
        });
      },
      error: (error: any) => {
        this.messageService.add({ severity: 'error', detail: error?.error?.title || 'Error al actualizar el miembro' });
      }
    });
  }

  deleteMember(member: any) {
    if (this.isFromContract) return;
    this.confirmationService.confirm({
      key: 'queueMemberConfirm',
      message: '¿Eliminar el miembro "' + (member.membername || member.interface) + '" de la cola?',
      header: 'Eliminar miembro',
      acceptLabel: 'Aceptar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        this.queuesMembersService.delete(member.id).subscribe({
          next: () => {
            this.messageService.add({ severity: 'success', detail: 'Miembro eliminado' });
            this.loadMembers();
          },
          error: (error: any) => {
            this.messageService.add({ severity: 'error', detail: error?.error?.title || 'Error al eliminar' });
          }
        });
      }
    });
  }

  cancel() {
    this.dynamicDialogRef.close();
  }
}
